import React, { useState } from 'react';
import { Search, Filter, ShoppingCart, Heart, Star, MapPin, Phone, Mail } from 'lucide-react';
import ProductDetailPage from './ProductDetailPage';
import CheckoutPage from './CheckoutPage';
import MiniCart from '../components/MiniCart';
import { useCart } from '../contexts/CartContext';

const StorePage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [showMiniCart, setShowMiniCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  
  const { addToCart, cartItems, updateQuantity, removeFromCart } = useCart();

  // 商品分类
  const categories = [
    { id: 'all', name: '全部', icon: '🛍️' },
    { id: 'food', name: '食品', icon: '🍽️' },
    { id: 'tea', name: '茶叶', icon: '🍵' },
    { id: 'wine', name: '酒水', icon: '🍷' },
  ];

  // 商品数据
  const products = [
    {
      id: 1,
      name: '小苍娃汆味呛鼻子登封小苍娃芥丝辣疙瘩丝介菜开胃咸菜下饭菜河南味道 500克芥丝3袋共3斤',
      price: 25.4,
      originalPrice: 38.8,
      image: '/images/jiecaisi.jpg',
      sales: 1234,
      rating: 4.8,
      category: 'food',
      inStock: true,
      isFavorite: false
    },
    {
      id: 2,
      name: '雅朴善太室本草黄精茶 道地养生茶 养生茶一盒',
      price: 199,
      originalPrice: 599.00,
      image: '/images/huangjingcha.jpg',
      sales: 856,
      rating: 4.6,
      category: 'tea',
      inStock: true,
      isFavorite: true
    },
    {
      id: 3,
      name: '张弓大汉盛运 浓香型白酒 52度 500ml*6瓶 整箱装',
      price: 910,
      originalPrice: 980,
      image: '/images/zhanggongjiu.jpg',
      sales: 567,
      rating: 4.5,
      category: 'wine',
      inStock: true,
      isFavorite: false
    },
    {
      id: 4,
      name: '鲲满坊贵州茅台镇 酱香型白酒 坤沙高粱酒整箱纯粮食53度品鉴酒 53度 500mL 6瓶 坤沙鉴赏酒',
      price: 586,
      originalPrice: 698,
      image: '/images/kunmanfang.jpg',
      sales: 432,
      rating: 4.7,
      category: 'wine',
      inStock: true,
      isFavorite: false
    }
  ];

    // 过滤和搜索商品
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // 排序商品
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'sales':
        return b.sales - a.sales;
      default:
        return b.sales - a.sales; // 默认按销量排序
    }
  });

  const renderProductCard = (product: any) => (
    <div 
      key={product.id} 
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => {
        setSelectedProduct(product);
        setShowProductDetail(true);
      }}
    >
      <div className="relative">
        <div className="w-full h-40 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          {product.image.startsWith('/') ? (
            <img 
              src={product.image} 
              alt={product.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : (
            <span className="text-6xl">{product.image}</span>
          )}
          {/* 备用显示，当图片加载失败时显示 */}
          <span className={`text-6xl ${product.image.startsWith('/') ? 'hidden' : ''}`}>
            {product.image}
          </span>
        </div>
        
        {/* 收藏按钮 */}
        <button className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-sm hover:bg-gray-50 transition-colors">
          <Heart 
            size={16} 
            className={product.isFavorite ? 'text-red-500 fill-current' : 'text-gray-400'} 
          />
        </button>
        
        {/* 库存状态 */}
        {!product.inStock && (
          <div className="absolute top-2 left-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
            缺货
          </div>
        )}
      </div>
      
      <div className="p-4">
        <h3 className="font-semibold text-gray-800 text-base mb-2 line-clamp-2 leading-tight">
          {product.name}
        </h3>
        
        <div className="flex items-center space-x-2 mb-3">
          <div className="flex items-center space-x-1">
            <Star size={14} className="text-yellow-400 fill-current" />
            <span className="text-sm text-gray-600">{product.rating}</span>
          </div>
          <span className="text-sm text-gray-400">|</span>
          <span className="text-sm text-gray-500">已售 {product.sales}</span>
        </div>
        
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="text-xl font-bold text-red-600">¥{product.price}</span>
            <span className="text-gray-400 text-sm line-through">¥{product.originalPrice}</span>
          </div>
        </div>
        
        <button 
          onClick={(e) => {
            e.stopPropagation();
            if (product.inStock) {
              addToCart({
                id: product.id,
                name: product.name,
                price: product.price,
                image: product.image
              });
            }
          }}
          className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
            product.inStock
              ? 'bg-primary text-white hover:bg-blue-600'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
          disabled={!product.inStock}
        >
          {product.inStock ? '加入购物车' : '暂时缺货'}
        </button>
      </div>
    </div>
  );

  const handleBackToStore = () => {
    setShowProductDetail(false);
    setSelectedProduct(null);
  };

  if (showCheckout) {
    return (
      <CheckoutPage 
        onBack={() => setShowCheckout(false)}
      />
    );
  }

  if (showProductDetail && selectedProduct) {
    return (
      <ProductDetailPage 
        productId={selectedProduct.id.toString()}
        onBack={handleBackToStore}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 头部搜索区域 */}
      <div className="bg-white border-b">
        <div className="p-4">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex-1 relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="搜索商品..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <Filter size={20} className="text-gray-600" />
            </button>
            <button 
              onClick={() => setShowMiniCart(true)}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors relative"
            >
              <ShoppingCart size={20} className="text-gray-600" />
              {cartItems.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              )}
            </button>
          </div>
          
          {/* 分类标签 */}
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-1 px-3 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span>{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 排序选项 */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">排序方式：</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="popular">按销量</option>
            <option value="rating">按评分</option>
            <option value="price-low">价格从低到高</option>
            <option value="price-high">价格从高到低</option>
          </select>
        </div>
      </div>

      {/* 商品列表 */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-4">
          {sortedProducts.map(renderProductCard)}
        </div>
        
        {sortedProducts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🔍</div>
            <p className="text-gray-500">没有找到相关商品</p>
            <p className="text-gray-400 text-sm">请尝试调整搜索条件或分类</p>
          </div>
        )}
      </div>

      {/* 商店信息 */}
      {/* <div className="bg-white border-t mt-6">
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">商店信息</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <MapPin size={20} className="text-gray-400" />
              <span className="text-gray-600">深圳市南山区科技园</span>
            </div>
            <div className="flex items-center space-x-3">
              <Phone size={20} className="text-gray-400" />
              <span className="text-gray-600">400-123-4567</span>
            </div>
            <div className="flex items-center space-x-3">
              <Mail size={20} className="text-gray-400" />
              <span className="text-gray-600">service@autounion.com</span>
            </div>
          </div>
        </div>
      </div> */}
      
      {/* 迷你购物车 */}
              <MiniCart
          isOpen={showMiniCart}
          onClose={() => setShowMiniCart(false)}
          cartItems={cartItems}
          onUpdateQuantity={updateQuantity}
          onRemoveItem={removeFromCart}
          onCheckout={() => {
            setShowMiniCart(false);
            setShowCheckout(true);
          }}
        />
    </div>
  );
};

export default StorePage;
